<template>
    <div class="app-wrapper">
        <!-- Consistent Glassmorphism Navbar -->
        <nav class="glass-nav nav-scrolled">
            <div class="nav-container">
                <router-link to="/" class="nav-logo">HP.</router-link>
                
                <div class="nav-links desktop-links">
                    <router-link to="/">Home</router-link>
                    <router-link to="/blogs" class="active">Blogs</router-link>
                    <ThemeToggle />
                </div>

                <!-- Mobile Menu Toggle -->
                <button class="menu-toggle" @click="isMenuOpen = !isMenuOpen" aria-label="Toggle menu">
                    <i :class="isMenuOpen ? 'bi bi-x-lg' : 'bi bi-list'"></i>
                </button>
            </div>

            <!-- Mobile Links Dropdown -->
            <transition name="fade">
                <div class="mobile-links" v-if="isMenuOpen">
                    <router-link to="/" @click="isMenuOpen = false">Home</router-link>
                    <router-link to="/blogs" @click="isMenuOpen = false" class="active">Blogs</router-link>
                    <div class="mobile-theme-toggle">
                        <ThemeToggle />
                    </div>
                </div>
            </transition>
        </nav>

        <main class="content-sections">
            <!-- Blogs Hero Section -->
            <section class="blogs-hero reveal">
                <div class="hero-bg"></div>
                <div class="container hero-content">
                    <span class="hero-eyebrow">Writings & Notes</span>
                    <h1 class="hero-title">Thoughts & <span class="accent">Insights</span></h1>
                    <p class="hero-subtitle">
                        Exploring the intersection of data science, healthcare research, 
                        and modern software engineering.
                    </p>
                </div>
            </section>

            <!-- Blogs List Section -->
            <section class="blogs-grid-section">
                <div class="container">
                    <div class="blogs-grid">
                        <div v-for="(blog, index) in blogs" 
                             :key="blog.slug" 
                             class="blog-card reveal"
                             :style="{ transitionDelay: `${index * 0.1}s` }">
                            
                            <router-link :to="`/blogs/${blog.slug}`" class="card-inner">
                                <div class="card-content">
                                    <h3 class="blog-card-title">{{ formatTitle(blog.slug) }}</h3>
                                    <p class="blog-excerpt">
                                        {{ getExcerpt(blog.content) }}
                                    </p>
                                    <div class="card-footer">
                                        <span class="read-more">Read Article <i class="bi bi-arrow-right"></i></span>
                                    </div>
                                </div>
                            </router-link>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Footer -->
            <footer class="main-footer">
                <div class="container">
                    <div class="footer-content">
                        <p class="footer-copy">&copy; 2026 Hitesh Pradhan. Crafted with passion.</p>
                        <div class="footer-socials">
                            <a href="https://github.com/pradhanhitesh"><i class="bi bi-github"></i></a>
                            <a href="https://www.linkedin.com/in/pradhanhitesh/"><i class="bi bi-linkedin"></i></a>
                            <a href="mailto:htshpradhan5@gmail.com"><i class="bi bi-envelope"></i></a>
                        </div>
                    </div>
                </div>
            </footer>
        </main>
    </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { loadBlogs } from '@/utils/blogLoader';
import ThemeToggle from "./ThemeToggle.vue";

const blogs = loadBlogs();
const isMenuOpen = ref(false);

const formatTitle = (slug) => {
    return slug
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const getExcerpt = (content) => {
    if (!content) return "Exploring technical concepts and research findings...";
    // Strip markdown-like characters and get first 100 chars
    const plainText = content.replace(/[#*`]/g, '').trim();
    return plainText.length > 120 ? plainText.substring(0, 120) + '...' : plainText;
};

const setupReveal = () => {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add("active");
            }
        });
    }, { threshold: 0.1 });

    document.querySelectorAll(".reveal").forEach(el => observer.observe(el));
};

onMounted(() => {
    setupReveal();
});
</script>

<style scoped>
.app-wrapper {
    background-color: var(--bg-primary);
    color: var(--text-main);
    font-family: 'Inter', sans-serif;
    line-height: 1.6;
    overflow-x: hidden;
    min-height: 100vh;
}

.container {
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 2rem;
}

/* Navbar */
.glass-nav {
    position: fixed;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    z-index: 1000;
    padding: 1rem 2rem;
    background: var(--nav-bg);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border-color);
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.nav-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    max-width: 1100px;
    margin: 0 auto;
    width: 100%;
}

.nav-logo {
    font-family: 'Outfit', sans-serif;
    font-size: 1.5rem;
    font-weight: 800;
    color: var(--text-main);
    text-decoration: none;
    letter-spacing: -1px;
}

.nav-links {
    display: flex;
    gap: 2rem;
}

.nav-links a {
    text-decoration: none;
    color: var(--text-muted);
    font-size: 0.9rem;
    font-weight: 500;
    transition: var(--transition);
}

.nav-links a:hover, .nav-links a.active {
    color: var(--accent);
}

/* Blogs Hero */
.blogs-hero {
    position: relative;
    padding: 10rem 0 6rem;
    text-align: center;
    overflow: hidden;
}

.hero-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 80% 20%, var(--accent-soft) 0%, transparent 40%),
                radial-gradient(circle at 10% 80%, var(--accent-soft) 0%, transparent 40%);
    z-index: -1;
}

.hero-eyebrow {
    display: block;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.2rem;
    color: var(--accent);
    margin-bottom: 1rem;
    font-weight: 600;
}

.hero-title {
    font-family: 'Outfit', sans-serif;
    font-size: 3.5rem;
    font-weight: 800;
    line-height: 1.1;
    margin-bottom: 1.5rem;
    letter-spacing: -1.5px;
}

.hero-title .accent {
    color: var(--accent);
}

.hero-subtitle {
    font-size: 1.15rem;
    color: var(--text-muted);
    max-width: 700px;
    margin: 0 auto;
}

/* Blogs Grid */
.blogs-grid-section {
    padding: 4rem 0 10rem;
}

.blogs-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2.5rem;
}

.blog-card {
    display: block;
    text-decoration: none;
    color: inherit;
}

.card-inner {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--glass);
    backdrop-filter: blur(10px);
    border: 1px solid var(--nav-border);
    border-radius: 2rem;
    padding: 2.5rem;
    transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
    text-decoration: none;
    color: inherit;
}

.card-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
}

.blog-card:hover .card-inner {
    transform: translateY(-12px);
    background: var(--card-bg);
    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.08);
    border-color: var(--accent);
}

.blog-card-title {
    font-family: 'Outfit', sans-serif;
    font-size: 1.4rem;
    font-weight: 700;
    margin-bottom: 1.25rem;
    line-height: 1.3;
}

.blog-excerpt {
    font-size: 0.95rem;
    color: var(--text-muted);
    margin-bottom: 2rem;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    flex-grow: 1; /* Pushes the footer down */
}

.card-footer {
    margin-top: auto;
    padding-top: 1rem;
}

.read-more {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--accent);
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: gap 0.3s ease;
}

.blog-card:hover .read-more {
    gap: 0.75rem;
}

/* Animations */
.reveal {
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
}

.reveal.active {
    opacity: 1;
    transform: translateY(0);
}

/* Responsive */
@media (max-width: 1024px) {
    .blogs-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 2rem;
    }
}

@media (max-width: 767px) {
    .blogs-grid {
        grid-template-columns: 1fr;
    }

    .hero-title {
        font-size: 2.75rem;
    }

    .blogs-hero {
        padding: 8rem 0 4rem;
    }

    .desktop-links {
        display: none;
    }

    .menu-toggle {
        display: block;
        background: none;
        border: none;
        font-size: 1.5rem;
        color: var(--text-main);
        cursor: pointer;
    }

    .mobile-links {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        padding: 1.5rem 0 1rem;
        border-top: 1px solid var(--border-color);
        margin-top: 0.5rem;
    }

    .mobile-links a {
        text-decoration: none;
        color: var(--text-main);
        font-weight: 500;
    }
}

/* Mobile Menu Logic Styling */
.menu-toggle {
    display: none;
}

.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

.mobile-theme-toggle {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid var(--border-color);
    display: flex;
    justify-content: flex-start;
}

/* Footer (Shared) */
.main-footer {
    padding: 1rem 0;
    border-top: 1px solid var(--border-color);
}

.footer-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.footer-copy {
    color: var(--text-muted);
    font-size: 0.9rem;
    margin: 0;
}

.footer-socials {
    display: flex;
    gap: 1.5rem;
}

.footer-socials a {
    color: var(--text-main);
    font-size: 1.25rem;
}
</style>